import type { Board } from "@gjwtrack/domain";
import { Plus } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import { Centered } from "../App.tsx";
import { TopBar } from "../components/TopBar.tsx";
import { client, useClient } from "../lib/client.ts";
import { BUNDLE_KINDS } from "../lib/format.ts";
import { Button, Dialog, DialogContent, DialogHeader, Input, InputSize, Panel } from "../ui/ring.ts";
import { SimpleSelect } from "../ui/SimpleSelect.tsx";
import { uuid } from "@gjwtrack/client";
import { ValueSelect } from "../ui/ValueSelect.tsx";

/**
 * `/boards`: YouTrack's /agiles does not list boards, it opens the one you used last (else the first one, favourites first).
 * Only with no board at all does the list page show; it is otherwise reachable as /agiles/all.
 */
export function BoardsEntry() {
  const lastBoardId = useClient((s) => s.me?.settings.lastBoardId);
  const favorites = useClient((s) => s.me?.settings.favoriteBoards);
  const [boards, setBoards] = useState<Board[] | null>(null);
  useEffect(() => {
    client.http.boards().then(setBoards).catch((e) => client.pushError("boards", (e as Error).message));
  }, []);
  if (boards === null) return <Centered>loading…</Centered>;
  const target = boards.find((b) => b.id === lastBoardId) ?? boards.find((b) => favorites?.includes(b.id)) ?? boards[0];
  if (target) return <Navigate to={`/agiles/${target.id}/current`} replace />;
  return <BoardsPage />;
}

/** `/agiles/:id` and the old `/board/:id` → the board's current sprint. */
export function BoardRedirect() {
  const { id = "" } = useParams();
  return <Navigate to={`/agiles/${id}/current`} replace />;
}

export function BoardsPage() {
  const [boards, setBoards] = useState<Board[] | null>(null);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const schema = useClient((s) => s.schema);
  useEffect(() => {
    client.http.boards().then(setBoards).catch((e) => client.pushError("boards", (e as Error).message));
  }, []);
  return (
    <div className="flex h-full flex-col">
      <TopBar crumbs={[{ label: "Agile Boards" }]} query="" onQuery={(q) => navigate(q ? `/issues?q=${encodeURIComponent(q)}` : "/issues")} onCreate={() => navigate("/issues")} />
      <div className="flex-1 overflow-auto px-8 py-5">
        <div className="mb-4 flex items-center gap-3">
          <h1 className="text-xl font-semibold tracking-tight">Agile Boards</h1>
          <div className="flex-1" />
          <Button primary icon={Plus} onClick={() => setCreating(true)}>
            New board
          </Button>
        </div>
        {boards === null ? (
          <div className="text-muted">Loading…</div>
        ) : boards.length === 0 ? (
          <div className="text-muted">No boards yet. Create one: pick the projects it covers and the field whose values become the columns.</div>
        ) : (
          <ul className="grid max-w-4xl grid-cols-2 gap-3">
            {boards.map((b) => (
              <li key={b.id}>
                <Link to={`/agiles/${b.id}/current`} className="block rounded-lg border border-line bg-surface p-4 hover:border-accent">
                  <div className="mb-1 text-[15px] font-semibold">{b.name}</div>
                  <div className="text-xs text-muted">
                    {b.projectIds.map((id) => schema?.projectsById.get(id)?.name ?? `#${id}`).join(", ") || "no projects"} · {b.columns.length}{" "}
                    {b.columns.length === 1 ? "column" : "columns"}
                  </div>
                  {b.query && <div className="mt-1 font-mono text-xs text-faint">{b.query}</div>}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      {creating && <NewBoardDialog onClose={() => setCreating(false)} onSaved={(b) => navigate(`/agiles/${b.id}/current?settings=1`)} />}
    </div>
  );
}

/**
 * New board: name, the projects it covers, and the field whose values are the
 * columns — everything else is set afterwards in the board's own settings panel,
 * which opens on arrival (YouTrack's "create, then configure in place").
 */
export function NewBoardDialog({ onClose, onSaved }: { onClose: () => void; onSaved: (b: Board) => void }) {
  const schema = useClient((s) => s.schema);
  const columnFields = (schema?.data.fields ?? []).filter((f) => BUNDLE_KINDS.has(f.kind) && f.bundleId !== undefined);
  const [name, setName] = useState("");
  const [projectIds, setProjectIds] = useState<number[]>([]);
  const [columnFieldId, setColumnFieldId] = useState<number>(columnFields.find((f) => f.kind === "state")?.id ?? columnFields[0]?.id ?? 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSaved(await client.http.command<Board>("createBoard", { name: name.trim(), projectIds, query: "", columnFieldId, swimlaneFieldId: null, estimationFieldId: null }, uuid()));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!schema) return null;
  return (
    <Dialog show label="New board" onCloseAttempt={onClose} trapFocus autoFocusFirst={false} contentClassName="!w-[30rem] !max-w-[calc(100vw-2rem)]">
      <DialogHeader>New board</DialogHeader>
      <DialogContent>
        <form id="new-board" onSubmit={(e) => void submit(e)} className="flex flex-col gap-3" aria-label="New board">
          <Input label="Name" size={InputSize.FULL} value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
          <label className="block">
            <span className="mb-1 block text-xs text-muted">Projects</span>
            <ValueSelect
              multi
              options={schema.data.projects.map((p) => ({ id: p.id, label: `${p.name} (${p.key})` }))}
              selected={new Set(projectIds)}
              onChange={setProjectIds}
              emptyLabel="Pick at least one project"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted">Columns by</span>
            <SimpleSelect full value={String(columnFieldId)} options={columnFields.map((f) => ({ value: String(f.id), label: f.name }))} onChange={(v) => setColumnFieldId(Number(v))} />
          </label>
          {error && <div className="rounded bg-danger-soft px-2 py-1 text-danger">{error}</div>}
        </form>
      </DialogContent>
      <Panel>
        <Button primary type="submit" form="new-board" disabled={busy || !name.trim() || !columnFieldId || projectIds.length === 0}>
          Create
        </Button>
        <Button onClick={onClose}>Cancel</Button>
      </Panel>
    </Dialog>
  );
}
